import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageSquare, MapPin, ArrowDown } from "lucide-react";
import { MaskReveal } from "./Reveal";
import { PHONE_DISPLAY, PHONE_TEL, TAGLINES, IMAGES } from "../../lib/site";

export const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 160]);
  const scaleBg = useTransform(scrollY, [0, 800], [1.1, 1.28]);
  const opacityContent = useTransform(scrollY, [0, 500], [1, 0]);

  const [tagIdx, setTagIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTagIdx((i) => (i + 1) % TAGLINES.length), 4200);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden flex items-center">
      {/* Parallax background */}
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Black 2021 Toyota Venza SUV on a coastal road at sunset" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/95 via-black/70 to-black/40" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black via-transparent to-black/60" />

      <motion.div style={{ opacity: opacityContent }} className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-[#D4AF37]" />
          <span className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">
            <MapPin size={13} /> Haymarket, Virginia
          </span>
        </motion.div>

        <h1 className="font-serif-display text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-4xl">
          <MaskReveal lines={["Reliable rides,", "delivered with"]} delay={0.35} now />
          <span className="block overflow-hidden pb-[0.08em]">
            <motion.span
              className="block text-gold-gradient italic"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
            >
              family care.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-8 h-7 overflow-hidden"
        >
          <motion.p
            key={tagIdx}
            initial={{ y: 28, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -28, opacity: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="hero-tagline"
            className="text-neutral-300 text-lg tracking-wide"
          >
            {TAGLINES[tagIdx]}
          </motion.p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-6 max-w-xl text-neutral-400 text-base leading-relaxed"
        >
          KAT Transportation Services is a family-operated ride service proudly serving Haymarket
          and surrounding neighborhoods — airport runs, medical appointments, and everyday trips.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="hero-call-button"
            className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] text-black px-8 py-4 font-semibold hover:bg-[#f3e5ab] transition-[background-color,transform] duration-300 hover:-translate-y-0.5"
          >
            <Phone size={18} strokeWidth={2.5} />
            Call {PHONE_DISPLAY}
          </a>
          <a
            href={`sms:${PHONE_TEL}`}
            data-testid="hero-text-button"
            className="inline-flex items-center justify-center gap-3 rounded-full border border-white/25 text-white px-8 py-4 font-medium hover:bg-white/5 hover:border-[#D4AF37] transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5"
          >
            <MessageSquare size={18} />
            Text Us
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-neutral-500"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-[#D4AF37]" />
        </motion.div>
      </motion.div>
    </section>
  );
};
